// Copyright (c) Proto Labs, Inc. All Rights Reserved.
import isEqual from 'lodash.isequal';
import { ErrorUtility } from './ErrorUtility';
class TokenManagerImpl {
    constructor(settings, logger, lock, state, eventPublisher, userManager, timeoutMonitor) {
        this.inTokenRenewal = false;
        this.user = {};
        this.throwIfFalsy(settings, 'settings');
        this.throwIfFalsy(logger, 'logger');
        this.throwIfFalsy(lock, 'lock');
        this.throwIfFalsy(state, 'state');
        this.throwIfFalsy(eventPublisher, 'eventPublisher');
        this.throwIfFalsy(userManager, 'userManager');
        this.throwIfFalsy(timeoutMonitor, 'timeoutMonitor');
        this.errorUtility = new ErrorUtility();
        this.lock = lock;
        this.logger = logger;
        this.publisher = eventPublisher;
        this.settings = settings;
        this.state = state;
        this.userManager = userManager;
        this.timeoutManager = timeoutMonitor;
        this.registerUserManagerEvents();
    }
    get renewingToken() {
        return this.inTokenRenewal;
    }
    get claims() {
        const user = this.user;
        return user && user.profile ? user.profile : {};
    }
    isHandshaking() {
        return (window.location.href.toLowerCase().indexOf(this.settings.redirectUri.toLowerCase()) ===
            0);
    }
    isOboSession() {
        const claims = this.claims;
        return !!claims && !!claims.amr && claims.amr.includes('obo');
    }
    isAuthenticated() {
        const user = this.user;
        return user && !!user.access_token;
    }
    /**
     * Initialize the tokenManager
     * @return {Promise<Boolean>} indicates if user is authenticated upon initialization
     */
    async initializeAsync() {
        const currentUser = await this.userManager.getUserAsync();
        if (!currentUser) {
            return false;
        }
        this.logger.info('TokenManager.initializeAsync: found existing user.');
        const oboId = new URLSearchParams(document.location.search).get(TokenManagerImpl.OboIndicator);
        if (oboId && oboId !== currentUser.profile.sub) {
            this.logger.info('TokenManager.initializeAsync: OBO customer changed. Existing session is invalid for the request.');
            await this.userManager.removeUserAsync();
            return false;
        }
        this.setUser(currentUser);
        return true;
    }
    /**
     * Begin the authentication process
     * @return {Promise<Boolean>} indicates if a redirect to auth has been trigggered
     */
    async startAuthenticationAsync(queryParams) {
        const currentUser = await this.userManager.getUserAsync();
        if (currentUser) {
            this.logger.info('TokenManager.startAuthenticationAsync: found existing user.');
            this.setUser(currentUser);
            return false;
        }
        if (this.state.signinAttempt === 0) {
            this.logger.info('TokenManager.startAuthenticationAsync: updating redirect route.');
            this.state.signinRedirect = this.determineActiveRoute();
            this.state.save();
        }
        this.logger.info('TokenManager.startAuthenticationAsync: starting signin.');
        const authArgs = this.getAuthenticationArgs(queryParams);
        await this.userManager.signinRedirectAsync(authArgs);
        return true;
    }
    /**
     * Complete the authentication process
     * @return {Promise}
     */
    async completeAuthenticationAsync() {
        let redirect;
        try {
            await this.userManager.signinCallbackAsync();
            redirect = this.state.signinRedirect;
            this.state.signinAttempt = 0;
        }
        catch (error) {
            this.logger.error(`TokenManager.completeAuthenticationAsync: ${this.errorUtility.getErrorMessage(error)}.`);
            if (this.state.signinAttempt <= this.settings.maxSigninAttempts) {
                this.logger.info('TokenManager.completeAuthenticationAsync: Attempting retry.');
                await this.userManager.removeUserAsync();
                redirect = this.state.signinRedirect;
                this.state.timeoutAt = undefined;
                this.state.signinAttempt++;
            }
            else {
                this.logger.error('TokenManager.completeAuthenticationAsync: All retries failed.');
                redirect = this.settings.errorRedirectUri;
                this.state.timeoutAt = undefined;
                this.state.signinAttempt = 0;
            }
        }
        finally {
            this.state.save();
        }
        return redirect;
    }
    /**
     * Get the access token
     * @return {Promise}
     */
    async getAccessTokenAsync(forceTokenRenew = false) {
        if (!this.user || !this.user.profile) {
            return '';
        }
        const currentUser = this.user;
        // If we're in the middle of renewing the token but the existing token is still valid, short circuit by returning the current/old token
        if (this.renewingToken &&
            currentUser.access_token &&
            currentUser.expires_in &&
            currentUser.expires_in > 1) {
            this.logger.info('TokenManager.getAccessTokenAsync: currently renewing; returning expiring but still valid token');
            return currentUser.access_token;
        }
        // Before we try to obtain a lock, check if we need to renew or not.
        if (forceTokenRenew ||
            !this.user.access_token ||
            (this.user.expires_in &&
                this.user.expires_in < this.settings.refreshAccessTokenExpireOffset)) {
            try {
                // We need to renew, try to obtain a lock.
                await this.lock.acquire(this.user.session_state, async () => {
                    // Now that we have a lock, make sure we still need to renew.
                    if (forceTokenRenew ||
                        !this.user.access_token ||
                        (this.user.expires_in &&
                            this.user.expires_in < this.settings.refreshAccessTokenExpireOffset)) {
                        const extraQueryParams = this.getAuthenticationArgs(null);
                        try {
                            this.inTokenRenewal = true;
                            await this.userManager.signinSilentAsync(extraQueryParams);
                            this.logger.info('TokenManager.getAccessTokenAsync: silently signed in.');
                            this.updateUserTimeout();
                        }
                        catch (error) {
                            const msg = this.errorUtility.getErrorMessage(error);
                            if (msg === 'login_required') {
                                if (this.timeoutManager.isTimedOut()) {
                                    this.logger.info('TokenManager.getAccessTokenAsync: login required, timing out.');
                                    await this.timeoutManager.timeout();
                                }
                                else {
                                    this.logger.info('TokenManager.getAccessTokenAsync: login required, signing out.');
                                    await this.signOutAsync();
                                }
                            }
                            else if (msg === 'invalid_grant') {
                                this.logger.info('TokenManager.getAccessTokenAsync: invalid grant, creating new session.');
                                await this.userManager.removeUserAsync();
                                await this.userManager.signinSilentAsync(extraQueryParams);
                                this.logger.info('TokenManager.getAccessTokenAsync: invalid grant, silently signed in.');
                                this.updateUserTimeout();
                            }
                            else {
                                this.logger.error(`TokenManager.getAccessTokenAsync: silent signin failed - ${error}`);
                            }
                        }
                        finally {
                            this.inTokenRenewal = false;
                        }
                    }
                });
            }
            catch (err) {
                this.logger.error(`TokenManager.getAccessTokenAsync: Failed during lock acquisition - ${err}`);
            }
        }
        return this.user &&
            this.user.access_token &&
            this.user.expires_in &&
            this.user.expires_in > 0
            ? this.user.access_token
            : '';
    }
    /**
     * Sign out the user.
     * @param {String} redirect
     */
    async signOutAsync() {
        this.logger.info('TokenManager.signOutAsync: clearing timeout.');
        this.timeoutManager.clear();
        if (this.isOboSession()) {
            this.logger.info('TokenManager.signOutAsync: revoking access token for obo session');
            await this.userManager.revokeAccessTokenAsync();
        }
        else {
            this.logger.info('TokenManager.signOutAsync: begin signout redirect');
            await this.userManager.signoutRedirectAsync();
        }
        this.clearUser();
    }
    clearUser() {
        if (this.user.profile) {
            this.user = {};
            this.publisher.publishAuthenticationChanged(false);
        }
    }
    setUser(newUser) {
        let isNewUser = true;
        let hasUpdatedClaims = false;
        let hasNewRefreshToken = false;
        if (this.user && this.user.profile && newUser && newUser.profile) {
            isNewUser = this.user.profile.sub !== newUser.profile.sub;
            hasUpdatedClaims = !isEqual(this.user.profile, newUser.profile);
            hasNewRefreshToken = this.user.refresh_token !== newUser.refresh_token;
        }
        this.logger.info(`TokenManager.setUser: updating user (expires_in: ${newUser.expires_in})`);
        this.user = newUser;
        if (!this.timeoutManager.timeoutAt || hasNewRefreshToken) {
            this.updateUserTimeout();
        }
        if (isNewUser) {
            this.publisher.publishAuthenticationChanged(true);
        }
        if (hasUpdatedClaims) {
            this.publisher.publishClaimsUpdated();
        }
    }
    updateUserTimeout() {
        if (!this.user) {
            this.timeoutManager.clear();
            return;
        }
        this.logger.info('TokenManager.updateUserTimeout: updating timeout...');
        const profileTimeoutSecs = parseInt(this.user.profile.protolabs_timeout); // Assuming that protolabs_timeout is measured in seconds
        const actualTimeoutSecs = this.settings.maxRefreshTokenExpireSeconds
            ? Math.min(profileTimeoutSecs, this.settings.maxRefreshTokenExpireSeconds)
            : profileTimeoutSecs;
        this.timeoutManager.update(actualTimeoutSecs);
    }
    determineActiveRoute() {
        const params = new URLSearchParams(document.location.search);
        params.delete(TokenManagerImpl.OboIndicator);
        let search = params.toString();
        if (search.length > 0) {
            search = `?${search}`;
        }
        const route = `${document.location.pathname}${search}${document.location.hash || ''}`;
        this.logger.info(`TokenManager.determineActiveRoute - route is '${route}'`);
        return route;
    }
    getAuthenticationArgs(queryParams) {
        const extraQueryParams = queryParams || {};
        const params = new URLSearchParams(document.location.search);
        TokenManagerImpl.DefaultCopySearchParams.forEach((key) => {
            if (params.has(key)) {
                extraQueryParams[key] = params.get(key);
            }
        });
        if (this.isOboSession()) {
            extraQueryParams[TokenManagerImpl.OboIndicator] = this.user.profile.sub;
        }
        if (this.timeoutManager.isFlagged()) {
            extraQueryParams.timeout = true;
        }
        return {
            extraQueryParams: extraQueryParams,
        };
    }
    registerUserManagerEvents() {
        this.userManager.registerAddUserLoaded((loadedUser) => {
            this.setUser(loadedUser);
        });
        this.userManager.registerAddUserSignedOut(() => {
            if (!this.isOboSession()) {
                this.signOutAsync();
            }
        });
        this.userManager.registerAddUserUnloaded(() => {
            this.clearUser();
        });
    }
    throwIfFalsy(value, name) {
        if (!value) {
            throw Error(`${name} must be a valid object instance`);
        }
    }
}
TokenManagerImpl.OboIndicator = 'obo_id';
TokenManagerImpl.DefaultCopySearchParams = [
    'obo_id',
    'lang',
    '_ga',
    'utm_medium',
    'utm_campaign',
    'utm_source',
    'utm_content',
    'utm_term',
];
export { TokenManagerImpl };
//# sourceMappingURL=TokenManagerImpl.js.map